/**
 * Per-type icons for workspace file chips. One source of truth shared by the
 * two chip renderers so they look identical:
 *   - MarkdownContent's `.papan-file-chip` (raw HTML, post-sanitize) → chipIconSvg()
 *   - FilePill (React twin, used in tool rows)                       → chipIconInner()
 *
 * Icons are stroke-based (fill:none, currentColor) to match the chip's text
 * color and the original generic file glyph. Category is chosen by extension;
 * anything unrecognized falls back to the generic document icon.
 */
import { isMiniappFolder, isProjectFolder, appDisplayName } from '@papan/types'

export type ChipIconCategory = 'app' | 'sheet' | 'code' | 'image' | 'data' | 'file'

const EXT_CATEGORY: Record<string, ChipIconCategory> = {
  // spreadsheets / tabular
  xlsx: 'sheet', xls: 'sheet', csv: 'sheet', tsv: 'sheet', ods: 'sheet', numbers: 'sheet',
  // code / text-structured
  py: 'code', js: 'code', ts: 'code', tsx: 'code', jsx: 'code', json: 'code', html: 'code',
  htm: 'code', css: 'code', scss: 'code', sh: 'code', bash: 'code', sql: 'code', yaml: 'code',
  yml: 'code', xml: 'code', toml: 'code', ipynb: 'code', java: 'code', c: 'code', cpp: 'code',
  go: 'code', rs: 'code', rb: 'code', php: 'code',
  // images
  png: 'image', jpg: 'image', jpeg: 'image', gif: 'image', svg: 'image', webp: 'image',
  bmp: 'image', ico: 'image', avif: 'image',
  // databases / columnar
  db: 'data', sqlite: 'data', sqlite3: 'data', duckdb: 'data', parquet: 'data', fdb: 'data',
  feather: 'data', arrow: 'data',
}

/** Category for a file name or full path (by extension). */
export function chipIconCategory(nameOrPath: string): ChipIconCategory {
  const base = nameOrPath.split('/').filter(Boolean).pop() || nameOrPath
  // A `.miniapp` folder is an app, not a document — give it the app glyph.
  if (isMiniappFolder(base)) return 'app'
  const dot = base.lastIndexOf('.')
  const ext = dot > 0 ? base.slice(dot + 1).toLowerCase() : ''
  return EXT_CATEGORY[ext] || 'file'
}

/** Display name for a chip — strips app/project markers so an app reads as
 *  "Lunch Poll", not "Lunch Poll.miniapp" or "Lunch Poll.miniapp.project". */
export function chipDisplayName(nameOrPath: string): string {
  const base = nameOrPath.split('/').filter(Boolean).pop() || nameOrPath
  return (isMiniappFolder(base) || isProjectFolder(base)) ? appDisplayName(base) : base
}

// Inner SVG markup only (no outer <svg>) — 24×24 viewBox, stroke-based.
const INNER: Record<ChipIconCategory, string> = {
  // Rounded-square app tile with a center dot — reads as a launchable app,
  // distinct from the document/code glyphs.
  app: '<rect x="3" y="3" width="18" height="18" rx="5"/><circle cx="12" cy="12" r="3"/>',
  sheet: '<rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/>',
  code: '<polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>',
  image: '<rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/>',
  data: '<ellipse cx="12" cy="5" rx="9" ry="3"/><path d="M3 5v14a9 3 0 0 0 18 0V5"/><path d="M3 12a9 3 0 0 0 18 0"/>',
  file: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/>',
}

/** Inner SVG markup for a chip icon (for React `dangerouslySetInnerHTML`). */
export function chipIconInner(nameOrPath: string): string {
  return INNER[chipIconCategory(nameOrPath)]
}

/** Full <svg> string (12px), matching the chip icon style. */
export function chipIconSvg(nameOrPath: string): string {
  return `<svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${chipIconInner(nameOrPath)}</svg>`
}
